import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Play, Star, Flame, Trophy, Sparkles, Brain } from 'lucide-react';
import { useActivities } from '../../hooks/useActivities';
import { useProgress } from '../../hooks/useProgress';
import { useDailyStreak } from '../../hooks/useDailyStreak';
import { useTodayProgress } from '../../hooks/useTodayProgress';
import { useChildStats } from '../../hooks/useChildStats';
import { useAIActivities } from '../../hooks/useAIActivities';
import { ActivityCard } from '../../components/ActivityCard';
import { AIActivityCard } from '../../components/AIActivityCard';
import { ActivitySimulator } from '../../components/ActivitySimulator';
import { AIActivitySimulator } from '../../components/AIActivitySimulator';
import Logo from '../../components/Logo';

interface ChildContextType {
  selectedChild: string;
  selectedChildData: any;
  soundEnabled: boolean;
  theme: any;
}

export function ChildHomePage() {
  const { selectedChild, selectedChildData, soundEnabled, theme } = useOutletContext<ChildContextType>();
  const { activities } = useActivities();
  const { addProgress } = useProgress(selectedChild);
  const { dailyStreak } = useDailyStreak(selectedChild);
  const { todayProgress, refetch: refetchTodayProgress } = useTodayProgress(selectedChild);
  const { stats, refetch: refetchStats } = useChildStats(selectedChild);
  const { aiActivities, markAsCompleted } = useAIActivities(selectedChild);
  const [showActivity, setShowActivity] = useState<string | null>(null);
  const [showAIActivity, setShowAIActivity] = useState<string | null>(null);

  const level = Math.floor(selectedChildData.total_points / 100) + 1;
  const progressToNextLevel = selectedChildData.total_points % 100;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const isDarkMode = theme.mode === 'dark' || theme.mode === 'high-contrast';

  const handleActivityComplete = async (score: number, timeSpent: number) => {
    if (!showActivity) return;

    const activity = activities.find(a => a.id === showActivity);
    if (!activity) return;

    const pointsEarned = Math.floor((score / 100) * activity.points_reward);

    await addProgress({
      child_id: selectedChild,
      activity_id: showActivity,
      score,
      time_spent: timeSpent,
      points_earned: pointsEarned,
    });

    refetchTodayProgress();
    refetchStats();
    setShowActivity(null);
  };

  const handleAIActivityComplete = async (score: number, timeSpent: number) => {
    if (!showAIActivity) return;

    const aiActivity = aiActivities.find(a => a.id === showAIActivity);
    if (!aiActivity) return;

    const pointsEarned = Math.floor((score / 100) * aiActivity.points_reward);

    await markAsCompleted(showAIActivity);
    await addProgress({
      child_id: selectedChild,
      activity_id: showAIActivity,
      score,
      time_spent: timeSpent,
      points_earned: pointsEarned,
    });

    refetchTodayProgress();
    refetchStats();
    setShowAIActivity(null);
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className={`rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl ${
        theme.mode === 'calm' 
          ? 'bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600'
          : theme.mode === 'warm'
          ? 'bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-600'
          : theme.mode === 'high-contrast'
          ? 'bg-gradient-to-r from-blue-600 to-purple-800 border-4 border-white'
          : isDarkMode 
          ? 'bg-gradient-to-r from-gray-800 via-blue-800 to-purple-800' 
          : 'bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'
      }`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-10 rounded-full translate-y-12 -translate-x-12"></div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl border-4 border-white border-opacity-30 shadow-lg"
                style={{ backgroundColor: selectedChildData.avatar_color }}
              >
                {selectedChildData.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-1 font-baloo">
                  {getGreeting()}, {selectedChildData.name}!
                </h2>
                <p className="text-lg opacity-90">
                  Ready for some fun learning?
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-4xl font-bold font-baloo">{selectedChildData.total_points}</div>
              <div className="opacity-90">Total Points</div>
            </div>
          </div>

          {/* Progress to next level */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-medium font-baloo">Level {level}</span>
              <span className="opacity-90">{progressToNextLevel}/100 to Level {level + 1}</span>
            </div>
            <div className="w-full bg-white bg-opacity-20 rounded-full h-4">
              <div
                className={`bg-white h-4 rounded-full shadow-sm ${
                  theme.reducedMotion ? '' : 'transition-all duration-500'
                }`}
                style={{ width: `${progressToNextLevel}%` }}
              />
            </div>
          </div>

          {/* Today's Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white bg-opacity-15 rounded-2xl p-4 text-center backdrop-blur-sm">
              <Flame className="w-8 h-8 text-orange-300 mx-auto mb-2" />
              <div className="text-2xl font-bold font-baloo">{dailyStreak?.current_streak || 0}</div>
              <div className="text-sm opacity-90">Day Streak</div>
            </div>
            
            <div className="bg-white bg-opacity-15 rounded-2xl p-4 text-center backdrop-blur-sm">
              <Trophy className="w-8 h-8 text-yellow-300 mx-auto mb-2" />
              <div className="text-2xl font-bold font-baloo">{todayProgress?.activitiesCompleted || 0}</div>
              <div className="text-sm opacity-90">Today's Activities</div>
            </div>
            
            <div className="bg-white bg-opacity-15 rounded-2xl p-4 text-center backdrop-blur-sm">
              <Star className="w-8 h-8 text-green-300 mx-auto mb-2" />
              <div className="text-2xl font-bold font-baloo">{todayProgress?.pointsEarned || 0}</div>
              <div className="text-sm opacity-90">Points Today</div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Activities Section */}
      {aiActivities.length > 0 && (
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[var(--text-primary)] font-baloo">
                Special Activities Just for You!
              </h3>
              <p className="text-lg text-[var(--text-secondary)]">
                These were made especially for you by Neuro, your brain buddy!
              </p>
            </div>
            <Sparkles className="w-6 h-6 text-purple-500" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {aiActivities.slice(0, 4).map((activity) => (
              <AIActivityCard
                key={activity.id}
                activity={activity}
                onStart={() => setShowAIActivity(activity.id)}
                completed={activity.status === 'completed'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Featured Activities */}
      <div>
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl">
            <Play className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-[var(--text-primary)] font-baloo">
              Fun Learning Activities
            </h3>
            <p className="text-lg text-[var(--text-secondary)]">
              Choose an activity to start your learning adventure!
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {activities.slice(0, 6).map((activity) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              onStart={() => setShowActivity(activity.id)}
              completed={stats.completedActivityIds.includes(activity.id)}
            />
          ))}
        </div>
      </div>

      {/* Activity Simulator Modal */}
      {showActivity && (
        <ActivitySimulator
          activity={activities.find(a => a.id === showActivity)!}
          onComplete={handleActivityComplete}
          onClose={() => setShowActivity(null)}
        />
      )}

      {/* AI Activity Simulator Modal */}
      {showAIActivity && (
        <AIActivitySimulator
          activity={aiActivities.find(a => a.id === showAIActivity)!}
          onComplete={handleAIActivityComplete}
          onClose={() => setShowAIActivity(null)}
        />
      )}
    </div>
  );
}